import { Head, Link, router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  ArrowLeft, 
  Edit,
  MessageCircleQuestion,
  Play,
  FileText,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface Document {
  id: number;
  name: string;
  'completed?'?: boolean;
}

interface ChunkCollection {
  id: number;
  name: string;
}

interface Chunk {
  id: number;
  document_id: number;
}

interface TestQuestion {
  id: number;
  question_text: string;
  answer_text?: string;
  status: string;
  metadata?: any;
  chunk_id: number;
}

interface TestQuestionShowProps {
  document: Document;
  chunk_collection: ChunkCollection | null;
  chunk: Chunk;
  test_question: TestQuestion;
  flash?: {
    notice?: string | null;
    alert?: string | null;
  };
}

export default function TestQuestionShow({ 
  document, 
  chunk_collection, 
  chunk, 
  test_question,
  flash
}: TestQuestionShowProps) {
  const breadcrumbItems = [
    { label: 'Documents', href: '/documents' },
    { label: document.name, href: `/documents/${document.id}` },
    { label: 'Test Questions', href: `/documents/${document.id}/questions` },
    { label: `Question ${test_question.id}`, href: '#' }
  ];

  const getStatusVariant = (status: string) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800',
      answered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    } as const;
    
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const handleAnswerQuestion = () => {
    router.post(`/documents/${document.id}/questions/${test_question.id}/answer`);
  };

  const formatMetadata = (metadata: any) => {
    if (!metadata) return 'No metadata available';
    return JSON.stringify(metadata, null, 2);
  };

  const chunkEditHref = `/documents/${document.id}/chunks/${chunk.id}/edit`;

  return (
    <>
      <Head title={`Test Question ${test_question.id} - ${document.name}`} />
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        {flash?.alert ? (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{flash.alert}</AlertDescription>
          </Alert>
        ) : null}
        {flash?.notice ? (
          <Alert className="border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>{flash.notice}</AlertDescription>
          </Alert>
        ) : null}

        <Breadcrumbs opsConsole items={breadcrumbItems} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button asChild variant="outline" size="sm">
              <Link href={`/documents/${document.id}/questions`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Questions
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <MessageCircleQuestion className="h-6 w-6 mr-2" />
                Test Question Details
              </h1>
              <p className="text-muted-foreground">
                Question {test_question.id} • Chunk {chunk.id}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {document['completed?'] && (
              <Button onClick={handleAnswerQuestion} className="bg-purple-600 hover:bg-purple-700">
                <Play className="h-4 w-4 mr-2" />
                Answer via DataRetriever
              </Button>
            )}
            
            {chunk_collection ? (
              <Button asChild variant="outline">
                <Link href={`/documents/${document.id}/chunk_collections/${chunk_collection.id}/chunks/${chunk.id}/test_questions/${test_question.id}/edit`}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Link>
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Question */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageCircleQuestion className="h-5 w-5 mr-2" />
                  Question
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {test_question.question_text}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Answer */}
            <Card>
              <CardHeader>
                <CardTitle>Answer</CardTitle>
              </CardHeader>
              <CardContent>
                {test_question.answer_text ? (
                  <div className="prose max-w-none">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {test_question.answer_text}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircleQuestion className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No answer provided yet</p>
                    <p className="text-sm mt-2">
                      Use "Answer via DataRetriever" to generate an answer
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            {test_question.metadata && (
              <Card>
                <CardHeader>
                  <CardTitle>Metadata</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="ordinal-scrollbar bg-muted p-4 rounded-md text-xs overflow-auto max-h-96">
                    {formatMetadata(test_question.metadata)}
                  </pre>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Info */}
            <Card>
              <CardHeader>
                <CardTitle>Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">
                    <Badge className={getStatusVariant(test_question.status)}>
                      {test_question.status}
                    </Badge>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Question ID</label>
                  <p className="mt-1 font-mono text-sm">{test_question.id}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Chunk ID</label>
                  <div className="mt-1">
                    <Button asChild variant="link" className="p-0 h-auto font-mono text-sm">
                      <Link href={chunkEditHref}>
                        {chunk.id}
                      </Link>
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Collection</label>
                  <div className="mt-1">
                    {chunk_collection ? (
                      <Button asChild variant="link" className="p-0 h-auto text-sm">
                        <Link href={`/documents/${document.id}/chunk_collections/${chunk_collection.id}/edit`}>
                          {chunk_collection.name || `Collection ${chunk_collection.id}`}
                        </Link>
                      </Button>
                    ) : (
                      <p className="text-sm text-muted-foreground">None</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button asChild variant="outline" size="sm" className="w-full justify-start">
                  <Link href={chunkEditHref}>
                    <FileText className="h-4 w-4 mr-2" />
                    View/Edit Chunk
                  </Link>
                </Button>
                
                {chunk_collection ? (
                  <Button asChild variant="outline" size="sm" className="w-full justify-start">
                    <Link href={`/documents/${document.id}/chunk_collections/${chunk_collection.id}/edit`}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Collection
                    </Link>
                  </Button>
                ) : null}
                
                <Button asChild variant="outline" size="sm" className="w-full justify-start">
                  <Link href={`/documents/${document.id}/questions`}>
                    <MessageCircleQuestion className="h-4 w-4 mr-2" />
                    All Questions
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
